import { useEffect } from "react";
import { useApp } from "@/context/AppContext";

const SITE = "https://pensioncheck.online";

/**
 * SEO sets <title>, meta description, og tags, hreflang and optional JSON-LD.
 * Pass `path` (e.g. "/", "/blog", "/frontaliers") so canonical + hreflang are correct.
 */
const SEO = ({ title, description, path = "/", image, jsonLd }) => {
  const { lang } = useApp();

  useEffect(() => {
    const fullTitle = title ? `${title} · Lux·Pension` : "Lux·Pension — Luxembourg Pension Estimator";
    document.title = fullTitle;
    document.documentElement.lang = lang;

    const setMeta = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [, k, v] = selector.match(/\[([^=]+)="([^"]+)"\]/) || [];
        if (k && v) el.setAttribute(k, v);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    const desc = description || "Estimate your Luxembourg private-sector pension — gross, deductions, net after taxes — in FR / EN / DE.";
    setMeta('meta[name="description"]', "content", desc);
    setMeta('meta[property="og:title"]', "content", fullTitle);
    setMeta('meta[property="og:description"]', "content", desc);
    setMeta('meta[property="og:type"]', "content", "website");
    setMeta('meta[property="og:url"]', "content", `${SITE}${path}`);
    setMeta('meta[property="og:locale"]', "content", lang === "fr" ? "fr_LU" : lang === "de" ? "de_LU" : "en_US");
    setMeta('meta[property="og:image"]', "content", image || `${SITE}/og-default.svg`);
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "content", fullTitle);
    setMeta('meta[name="twitter:description"]', "content", desc);
    setMeta('meta[name="twitter:image"]', "content", image || `${SITE}/og-default.svg`);

    // Canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = `${SITE}${path}`;

    // hreflang alternates
    document.head.querySelectorAll('link[rel="alternate"][data-seo-hreflang]').forEach((n) => n.remove());
    ["fr", "en", "de"].forEach((l) => {
      const link = document.createElement("link");
      link.rel = "alternate";
      link.hreflang = l;
      link.href = `${SITE}${path}?lang=${l}`;
      link.setAttribute("data-seo-hreflang", "1");
      document.head.appendChild(link);
    });
    const xDef = document.createElement("link");
    xDef.rel = "alternate";
    xDef.hreflang = "x-default";
    xDef.href = `${SITE}${path}`;
    xDef.setAttribute("data-seo-hreflang", "1");
    document.head.appendChild(xDef);

    // JSON-LD
    document.head.querySelectorAll("script[data-seo-jsonld]").forEach((n) => n.remove());
    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo-jsonld", "1");
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, path, image, jsonLd, lang]);

  return null;
};

export default SEO;
